import React, {useEffect, useMemo, useState} from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Chip,
  Alert,
  Snackbar,
  TextField,
  MenuItem,
  FormControlLabel,
  Switch,
  Stack,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {useNavigate, useParams} from 'react-router-dom';
import {useMutation, useQuery} from '@apollo/client';
import {
  GET_WEB_CRAWL,
  GET_WEB_CRAWL_PAGES,
  LIST_GUIDES,
} from '../graphql/queries';
import {
  CREATE_ORGANIZATION_BRAND_PROFILE,
  SET_GUIDE_THEME,
} from '../graphql/mutations';
import {useAuth} from '../contexts/AuthContext';
import {useDocumentTitle} from '../hooks/useDocumentTitle';
import {colors} from '../theme/colors';
import BrandingReviewCard, {BrandingSelection} from '../components/branding/BrandingReviewCard';

function statusColor(status: string): 'default' | 'info' | 'success' | 'error' {
  switch (status) {
    case 'QUEUED': return 'default';
    case 'RUNNING': return 'info';
    case 'SUCCEEDED': return 'success';
    case 'FAILED': return 'error';
    default: return 'default';
  }
}

function downloadStatusColor(status: string): 'default' | 'info' | 'success' | 'error' | 'warning' {
  switch (status) {
    case 'pending': return 'default';
    case 'downloaded': return 'info';
    case 'imported': return 'success';
    case 'failed': return 'error';
    default: return 'default';
  }
}

function formatBytes(bytes?: number): string {
  if (!bytes) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const APPROVAL_CONFIDENCE_THRESHOLD = 0.65;

export default function WebCrawlDetailPage() {
  useDocumentTitle('Web Crawl');

  const {user} = useAuth();
  const navigate = useNavigate();
  const {crawlId} = useParams<{crawlId: string}>();
  const [profileName, setProfileName] = useState('');
  const [selectedGuideId, setSelectedGuideId] = useState('');
  const [savedProfileId, setSavedProfileId] = useState<string>('');
  const [brandSelection, setBrandSelection] = useState<BrandingSelection>({
    organizationDisplayName: '',
    logoUrl: '',
    headerLogoUrl: '',
    primaryColor: '#2563eb',
    secondaryColor: '#dbeafe',
    tone: 'NEUTRAL',
  });
  const [approvedForGuideUse, setApprovedForGuideUse] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({open: false, message: '', severity: 'info'});

  const isActive = (status: string) => status === 'QUEUED' || status === 'RUNNING';

  const {data, loading} = useQuery(GET_WEB_CRAWL, {
    variables: {id: crawlId},
    skip: !user || !crawlId,
    pollInterval: 3000,
  });

  const crawl = data?.webCrawl;

  const {data: guidesData} = useQuery(LIST_GUIDES, {
    skip: !user || !crawl,
  });
  const guides = guidesData?.listGuides || [];

  const {data: pagesData} = useQuery(GET_WEB_CRAWL_PAGES, {
    variables: {crawlId},
    skip: !user || !crawlId || !crawl || isActive(crawl?.status),
    pollInterval: crawl && isActive(crawl.status) ? 5000 : 0,
  });

  const pages = useMemo(() => pagesData?.webCrawlPages || [], [pagesData]);
  const discovery = crawl?.brandExtraction;
  const defaultProfileName = useMemo(() => {
    const discoveredName = discovery?.selectedCandidates?.organizationDisplayName;
    if (discoveredName && discoveredName.trim()) {
      return `${discoveredName.trim()} imported profile`;
    }
    return 'Imported brand profile';
  }, [discovery]);
  useEffect(() => {
    if (!discovery?.selectedCandidates) return;
    setSavedProfileId('');
    setBrandSelection({
      organizationDisplayName: discovery.selectedCandidates.organizationDisplayName || '',
      logoUrl: discovery.selectedCandidates.logoUrl || '',
      headerLogoUrl: discovery.selectedCandidates.headerLogoUrl || '',
      primaryColor: discovery.selectedCandidates.primaryColor || '#2563eb',
      secondaryColor: discovery.selectedCandidates.secondaryColor || '#dbeafe',
      tone: discovery.selectedCandidates.tone || 'NEUTRAL',
    });
    setApprovedForGuideUse((discovery.confidence || 0) >= APPROVAL_CONFIDENCE_THRESHOLD);
  }, [discovery]);
  useEffect(() => {
    if (!discovery) return;
    setSavedProfileId('');
  }, [approvedForGuideUse, brandSelection, profileName, discovery]);

  const [createBrandProfile, {loading: creatingProfile}] = useMutation(CREATE_ORGANIZATION_BRAND_PROFILE);
  const [setGuideTheme, {loading: applyingGuideTheme}] = useMutation(SET_GUIDE_THEME);

  // Collect all unique documents across pages
  const allDocuments = React.useMemo(() => {
    const seen = new Set<string>();
    const docs: any[] = [];
    for (const page of pages) {
      for (const doc of page.documents || []) {
        if (!seen.has(doc.url)) {
          seen.add(doc.url);
          docs.push(doc);
        }
      }
    }
    return docs;
  }, [pages]);

  const showMessage = (
    message: string,
    severity: 'success' | 'error' | 'info'
  ) => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const saveDiscoveredBrandProfile = async (): Promise<string | null> => {
    if (!crawl || !discovery?.selectedCandidates) {
      showMessage('No discovered brand is available for this crawl.', 'error');
      return null;
    }
    const profileLabel = profileName.trim() || defaultProfileName;
    const status = approvedForGuideUse ? 'APPROVED' : 'DRAFT';
    try {
      const result = await createBrandProfile({
        variables: {
          input: {
            orgId: crawl.orgId,
            name: profileLabel,
            status,
            setAsDefault: false,
            organizationDisplayName: brandSelection.organizationDisplayName || null,
            logoUrl: brandSelection.logoUrl || null,
            headerLogoUrl: brandSelection.headerLogoUrl || null,
            palette: {
              primary: brandSelection.primaryColor || '#2563eb',
              secondary: brandSelection.secondaryColor || '#dbeafe',
              background: '#f9fafb',
              surface: '#ffffff',
              text: '#111827',
            },
            tone: brandSelection.tone || 'NEUTRAL',
            confidenceScore: discovery.confidence,
            createdFromImportId: crawl.id,
            provenance: [
              `crawl:${crawl.id}`,
              `source:${crawl.seedUrl}`,
              approvedForGuideUse ? 'reviewed:manual' : 'reviewed:pending',
            ],
          },
        },
      });
      const createdProfileId = result.data?.createOrganizationBrandProfile?.id;
      if (!createdProfileId) {
        showMessage('Profile was created but no profile ID was returned.', 'error');
        return null;
      }
      setSavedProfileId(createdProfileId);
      showMessage(
        approvedForGuideUse
          ? 'Discovered brand saved as approved organization profile.'
          : 'Discovered brand saved as draft profile. Approve before guide usage.',
        approvedForGuideUse ? 'success' : 'info'
      );
      return createdProfileId;
    } catch (error: any) {
      showMessage(error.message || 'Failed to save discovered brand profile.', 'error');
      return null;
    }
  };

  const handleUseForGuide = async () => {
    if (!selectedGuideId) {
      showMessage('Select a guide before applying branding.', 'error');
      return;
    }
    if (!approvedForGuideUse) {
      showMessage('Review and approve the discovered brand before applying it to a guide.', 'error');
      return;
    }
    let profileId = savedProfileId;
    if (!profileId) {
      const created = await saveDiscoveredBrandProfile();
      if (!created) return;
      profileId = created;
    }
    try {
      await setGuideTheme({
        variables: {
          guideId: selectedGuideId,
          input: {
            brandMode: 'IMPORT_PROFILE',
            brandProfileId: profileId,
          },
        },
      });
      showMessage('Discovered brand applied to selected guide.', 'success');
    } catch (error: any) {
      showMessage(error.message || 'Failed to apply discovered brand to guide.', 'error');
    }
  };

  if (loading) {
    return (
      <Box sx={{display: 'flex', justifyContent: 'center', py: 8}}>
        <CircularProgress />
      </Box>
    );
  }

  if (!crawl) {
    return (
      <Box sx={{py: 4, px: 3}}>
        <Typography sx={{fontSize: 20, fontWeight: 600}}>Crawl not found</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{maxWidth: 1040, margin: '0 auto', padding: '24px 24px 64px'}}>
      {/* Back button */}
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/org/crawls')}
        sx={{mb: 2, textTransform: 'none', color: '#6B7280'}}
      >
        Back to crawls
      </Button>

      {/* Header */}
      <Box sx={{mb: 3}}>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5, mb: 1}}>
          <Typography sx={{fontSize: 20, fontWeight: 650, color: colors.textPrimary}}>
            Web Crawl
          </Typography>
          <Chip
            label={crawl.status}
            size="small"
            color={statusColor(crawl.status)}
            variant={crawl.status === 'RUNNING' ? 'filled' : 'outlined'}
            sx={{height: 22, fontSize: 12}}
          />
        </Box>
        <Typography
          component="a"
          href={crawl.seedUrl}
          target="_blank"
          rel="noopener noreferrer"
          sx={{fontSize: 14, color: colors.blue?.[600] || '#2563EB', textDecoration: 'none', '&:hover': {textDecoration: 'underline'}}}
        >
          {crawl.seedUrl}
        </Typography>
      </Box>

      {/* Stats */}
      <Box
        sx={{
          bgcolor: '#fff',
          border: '1px solid #E5E7EB',
          borderRadius: '12px',
          p: 2.5,
          mb: 3,
        }}
      >
        <Typography sx={{fontSize: 14, fontWeight: 600, mb: 1.5}}>Details</Typography>
        <Box sx={{display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2}}>
          {[
            {label: 'Pages Crawled', value: crawl.pagesCrawled},
            {label: 'Docs Found', value: crawl.documentsFound},
            {label: 'Docs Imported', value: crawl.documentsImported},
            {label: 'Max Depth', value: crawl.maxDepth},
          ].map(stat => (
            <Box key={stat.label}>
              <Typography sx={{fontSize: 12, color: '#9CA3AF', fontWeight: 600}}>{stat.label.toUpperCase()}</Typography>
              <Typography sx={{fontSize: 18, fontWeight: 600}}>{stat.value}</Typography>
            </Box>
          ))}
        </Box>

        {crawl.generationJobId && (
          <Button
            size="small"
            onClick={() => navigate(`/org/jobs/${crawl.generationJobId}`)}
            sx={{mt: 2, textTransform: 'none'}}
          >
            View background job
          </Button>
        )}
      </Box>

      {/* Discovered brand */}
      {discovery && (
        <Box sx={{mb: 3}}>
          <BrandingReviewCard
            title="Discovered brand"
            subtitle="Discovery does not auto-apply. Save or apply explicitly."
            confidence={discovery.confidence}
            warnings={discovery.warnings || []}
            selection={brandSelection}
            editable
            onChange={setBrandSelection}
            actions={(
              <Stack spacing={1.25}>
                <TextField
                  label="Profile name"
                  size="small"
                  value={profileName}
                  placeholder={defaultProfileName}
                  onChange={event => setProfileName(event.target.value)}
                />
                <FormControlLabel
                  control={(
                    <Switch
                      checked={approvedForGuideUse}
                      onChange={event => setApprovedForGuideUse(event.target.checked)}
                    />
                  )}
                  label="I reviewed this brand and approve it for guide usage"
                />
                <Box sx={{display: 'grid', gridTemplateColumns: '1fr auto auto', gap: 1}}>
                  <TextField
                    select
                    label="Use for this guide"
                    size="small"
                    value={selectedGuideId}
                    onChange={event => setSelectedGuideId(event.target.value)}
                  >
                    <MenuItem value="">Select guide</MenuItem>
                    {guides.map((guide: any) => (
                      <MenuItem key={guide.id} value={guide.id}>
                        {guide.title}
                      </MenuItem>
                    ))}
                  </TextField>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      saveDiscoveredBrandProfile();
                    }}
                    disabled={creatingProfile}
                  >
                    Save as org profile
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleUseForGuide}
                    disabled={creatingProfile || applyingGuideTheme}
                  >
                    Use for this guide
                  </Button>
                </Box>
              </Stack>
            )}
          />
        </Box>
      )}

      {/* Sitemap section */}
      {pages.length > 0 && (
        <Box
          sx={{
            bgcolor: '#fff',
            border: '1px solid #E5E7EB',
            borderRadius: '12px',
            p: 2.5,
            mb: 3,
          }}
        >
          <Typography sx={{fontSize: 14, fontWeight: 600, mb: 1.5}}>
            Sitemap ({pages.length} pages)
          </Typography>
          {pages.map((page: any) => (
            <Box
              key={page.id}
              sx={{
                py: 0.75,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                borderBottom: '1px solid #F3F4F6',
                '&:last-child': {borderBottom: 'none'},
              }}
            >
              <Box sx={{width: 24, textAlign: 'center'}}>
                <Typography sx={{fontSize: 11, color: '#9CA3AF'}}>{page.depth}</Typography>
              </Box>
              <Box sx={{flex: 1, minWidth: 0}}>
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 500,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {page.title || page.url}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 12,
                    color: '#9CA3AF',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {page.url}
                </Typography>
              </Box>
              {page.documents?.length > 0 && (
                <Chip
                  label={`${page.documents.length} doc${page.documents.length !== 1 ? 's' : ''}`}
                  size="small"
                  variant="outlined"
                  sx={{height: 20, fontSize: 11}}
                />
              )}
            </Box>
          ))}
        </Box>
      )}

      {/* Documents section */}
      {allDocuments.length > 0 && (
        <Box
          sx={{
            bgcolor: '#fff',
            border: '1px solid #E5E7EB',
            borderRadius: '12px',
            p: 2.5,
            mb: 3,
          }}
        >
          <Typography sx={{fontSize: 14, fontWeight: 600, mb: 1.5}}>
            Discovered Documents ({allDocuments.length})
          </Typography>

          {/* Table header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              pb: 0.75,
              mb: 0.5,
              borderBottom: '1px solid #E5E7EB',
            }}
          >
            {[
              {label: 'Filename', flex: 1, width: undefined},
              {label: 'Type', flex: undefined, width: 80},
              {label: 'Size', flex: undefined, width: 80},
              {label: 'Status', flex: undefined, width: 90},
              {label: '', flex: undefined, width: 40},
            ].map((col, i) => (
              <Typography
                key={i}
                sx={{
                  flex: col.flex,
                  width: col.width,
                  fontSize: 11,
                  fontWeight: 600,
                  color: '#9CA3AF',
                  textTransform: 'uppercase',
                }}
              >
                {col.label}
              </Typography>
            ))}
          </Box>

          {allDocuments.map((doc: any) => (
            <Box
              key={doc.url}
              sx={{
                py: 0.75,
                display: 'flex',
                alignItems: 'center',
                borderBottom: '1px solid #F3F4F6',
                '&:last-child': {borderBottom: 'none'},
              }}
            >
              <Box sx={{flex: 1, minWidth: 0}}>
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 500,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {doc.filename}
                </Typography>
              </Box>
              <Typography sx={{width: 80, fontSize: 12, color: '#6B7280'}}>
                {doc.mimeType?.split('/').pop()?.toUpperCase() || '-'}
              </Typography>
              <Typography sx={{width: 80, fontSize: 12, color: '#6B7280'}}>
                {formatBytes(doc.sizeBytes)}
              </Typography>
              <Box sx={{width: 90}}>
                <Chip
                  label={doc.downloadStatus}
                  size="small"
                  color={downloadStatusColor(doc.downloadStatus)}
                  variant="outlined"
                  sx={{height: 20, fontSize: 10}}
                />
              </Box>
              <Box sx={{width: 40, display: 'flex', justifyContent: 'center'}}>
                {doc.sourceItemId && (
                  <Button
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate('/org/sources');
                    }}
                    sx={{minWidth: 0, p: 0.5}}
                  >
                    <OpenInNewIcon sx={{fontSize: 14}} />
                  </Button>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {crawl.status === 'RUNNING' && (
        <Alert severity="info" sx={{mb: 3}}>
          Crawl is in progress. This page will update automatically.
        </Alert>
      )}

      {crawl.status === 'FAILED' && (
        <Alert severity="error">
          Crawl failed. Check the background job for details.
        </Alert>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({...prev, open: false}))}
        anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar(prev => ({...prev, open: false}))}
          sx={{width: '100%'}}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
